import { Preferences } from "@capacitor/preferences";
import { Drivers, Storage } from "@ionic/storage";
import CordovaSQLiteDriver from "localforage-cordovasqlitedriver";
import {
  AdherenceData,
  CurveAnalysisData,
  StoredAdherenceData,
  StoredCurveAnalysisData,
  StoredCurveSession,
  StoredTitrationSession,
  StoredTitrationSessionsList,
  TherapySession,
  TitrationData,
  TitrationSession,
} from "../contexts/types";
export const storePrefrences = async (key: string, value: any) => {
  const storedValue =
    typeof value === "object" && value !== null ? JSON.stringify(value) : value;

  return await Preferences.set({
    key: key,
    value: storedValue,
  });
};
export const getStoredPreferences = async (key: string): Promise<any> => {
  const result = await Preferences.get({ key });
  if (result.value) {
    try {
      return JSON.parse(result.value);
    } catch (error) {
      console.error(`Error parsing JSON for key ${key}:`, error, result.value);
      return result.value; // Return raw value if parsing fails
    }
  }
  return null; // Return null if no value is found
};
export const clearStoredPreferences = async (key: string): Promise<void> => {
  await Preferences.remove({ key });
};
// Define therapy interface
export interface TherapyEntry {
  therapy: string;
  event_id: string;
  batch_id: string;
  timestamp: number; // Store creation time for sorting
}

// Define the structure for batch metadata
export interface BatchMetadata {
  batch_id: string;
  timestamp: number;
  entryCount: number;
}

// Updated storage functions for storageService.ts

export const initStorage = async (storage: Storage | null) => {
  let storageInstance: Storage;
  if (!storage) {
    storageInstance = new Storage({
      driverOrder: [
        CordovaSQLiteDriver._driver,
        Drivers.IndexedDB,
        Drivers.LocalStorage,
      ],
    });
    await storageInstance.defineDriver(CordovaSQLiteDriver);
  } else {
    storageInstance = storage;
  }
  // Always ensure .create() is called!
  await storageInstance.create();
  return storageInstance;
};

export const saveDeviceToStorage = async (
  storage: Storage | null,
  deviceId: string,
  deviceName: string,
  serialNumber?: string,
  deviceType = "Cough"
) => {
  if (storage) {
    const deviceSerialNumber = serialNumber || deviceId;
    await storage.set("connectedDevices", {
      deviceId,
      deviceName,
      deviceSerialNumber,
      deviceType,
    });
  }
};

export const loadDeviceFromStorage = async (storage: Storage | null) => {
  if (storage) {
    return await storage.get("connectedDevices");
  }
  return null;
};

export const savePulseOximeterToStorage = async (
  storage: Storage | null,
  deviceId: string,
  deviceName: string,
  serialNumber?: string,
  deviceType = "Pulse Oximeter"
) => {
  if (storage) {
    const deviceSerialNumber = serialNumber || deviceId;
    await storage.set("lastConnectedPulseOximeterDevice", {
      deviceId,
      deviceName,
      deviceSerialNumber,
      deviceType,
    });
  }
};

export const loadPulseOximeterFromStorage = async (storage: Storage | null) => {
  if (storage) {
    return await storage.get("lastConnectedPulseOximeterDevice");
  }
  return null;
};

/**
 * Initialize batches metadata if it doesn't exist
 */
const initBatchesMetadata = async (
  storage: Storage
): Promise<BatchMetadata[]> => {
  const batches = (await storage.get("therapy_batches")) || [];
  return batches;
};

/**
 * Store a new therapy entry with batch_id and event_id
 * Maintains only the last 5 batches by automatically pruning old ones
 *
 * @param storage The Ionic Storage instance
 * @param therapy The therapy string to store
 * @param event_id Unique identifier for this specific therapy event
 * @param batch_id Identifier for grouping related events
 */
export const storeTherapy = async (
  storage: Storage | null,
  therapy: string,
  event_id: string,
  batch_id: string
) => {
  if (!storage) return;

  const timestamp = Date.now();
  const therapyEntry: TherapyEntry = {
    therapy,
    event_id,
    batch_id,
    timestamp,
  };

  // 1. Get existing therapies map or create a new one
  const therapiesMap = (await storage.get("therapies_map")) || {};

  // 2. Get or update batch metadata
  let batchesMetadata: BatchMetadata[] = await initBatchesMetadata(storage);

  // Check if this batch exists
  const existingBatchIndex = batchesMetadata.findIndex(
    (b) => b.batch_id === batch_id
  );

  if (existingBatchIndex === -1) {
    // Add new batch metadata
    batchesMetadata.push({
      batch_id,
      timestamp,
      entryCount: 1,
    });
    // Sort batches by timestamp (newest first)
    batchesMetadata.sort((a, b) => parseInt(b.batch_id) - parseInt(a.batch_id));
  } else {
    // Update existing batch
    if (!therapiesMap[batch_id]?.[event_id]) {
      batchesMetadata[existingBatchIndex].entryCount++;
    }
  }

  // 3. Store the therapy entry with both batch and event indexing
  // Create batch entry if it doesn't exist
  if (!therapiesMap[batch_id]) {
    therapiesMap[batch_id] = {};
  }

  // Add therapy to the specific event_id within the batch
  therapiesMap[batch_id][event_id] = therapyEntry;

  // 4. Prune excess batches (keep only the last 5)
  if (batchesMetadata.length > 5) {
    const batchesToRemove = batchesMetadata.slice(5);

    // Remove excess batches from storage
    for (const batchMeta of batchesToRemove) {
      const batchId = batchMeta.batch_id;
      delete therapiesMap[batchId];
    }

    // Update batch metadata to only keep the last 5
    batchesMetadata = batchesMetadata.slice(0, 5);
  }

  // 5. Save updated data back to storage
  await storage.set("therapies_map", therapiesMap);
  await storage.set("therapy_batches", batchesMetadata);
};

/**
 * Retrieve all stored therapies across all batches
 */
export const getAllTherapies = async (
  storage: Storage | null
): Promise<TherapyEntry[]> => {
  if (!storage) return [];

  const therapiesMap = (await storage.get("therapies_map")) || {};
  const allTherapies: TherapyEntry[] = [];

  // Flatten the nested structure into an array
  for (const batchId in therapiesMap) {
    for (const eventId in therapiesMap[batchId]) {
      allTherapies.push(therapiesMap[batchId][eventId]);
    }
  }

  // Sort by timestamp (newest first)
  return allTherapies.sort((a, b) => b.timestamp - a.timestamp);
};

/**
 * Retrieve therapies by batch_id
 */
export const getTherapiesByBatch = async (
  storage: Storage | null,
  batchId: string
): Promise<TherapyEntry[]> => {
  if (!storage) return [];

  const therapiesMap = (await storage.get("therapies_map")) || {};
  const batchTherapies: TherapyEntry[] = [];

  if (therapiesMap[batchId]) {
    for (const eventId in therapiesMap[batchId]) {
      batchTherapies.push(therapiesMap[batchId][eventId]);
    }
  }

  return batchTherapies.sort((a, b) => b.timestamp - a.timestamp);
};

/**
 * Retrieve a therapy by event_id (across all batches)
 */
export const getTherapyByEventId = async (
  storage: Storage | null,
  eventId: string
): Promise<TherapyEntry | null> => {
  if (!storage) return null;

  const therapiesMap = (await storage.get("therapies_map")) || {};

  // Search through all batches for the event_id
  for (const batchId in therapiesMap) {
    if (therapiesMap[batchId][eventId]) {
      return therapiesMap[batchId][eventId];
    }
  }

  return null;
};

/**
 * Count therapies by event_id (for aggregation)
 */
export const countTherapiesByEvent = async (
  storage: Storage | null
): Promise<Record<string, number>> => {
  if (!storage) return {};

  const therapiesMap = (await storage.get("therapies_map")) || {};
  const counts: Record<string, number> = {};

  for (const batchId in therapiesMap) {
    for (const eventId in therapiesMap[batchId]) {
      counts[eventId] = (counts[eventId] || 0) + 1;
    }
  }

  return counts;
};

/**
 * Get metadata about stored batches
 */
export const getBatchesMetadata = async (
  storage: Storage | null
): Promise<BatchMetadata[]> => {
  if (!storage) return [];
  return (await storage.get("therapy_batches")) || [];
};

/**
 * Clear all therapy data
 */
export const clearTherapyData = async (
  storage: Storage | null
): Promise<void> => {
  if (!storage) return;

  await storage.remove("therapies_map");
  await storage.remove("therapy_batches");
};

/**
 * Legacy compatibility method - get the most recent event in the most recent batch
 * Maintains backwards compatibility with existing code
 */
export const getTherapies = async (
  storage: Storage | null
): Promise<{ batch_id: string; therapy: string } | null> => {
  if (!storage) return null;

  const batchesMetadata = (await storage.get("therapy_batches")) || [];

  if (batchesMetadata.length === 0) return null;

  // Get most recent batch_id
  const mostRecentBatch = batchesMetadata[0];
  const therapies = await getTherapiesByBatch(
    storage,
    mostRecentBatch.batch_id
  );

  // If no therapies in this batch, return null
  if (therapies.length === 0) return null;

  // Sort therapies by timestamp to get the most recent one
  const mostRecentTherapy = therapies.sort(
    (a, b) => b.timestamp - a.timestamp
  )[0];

  // Format in the legacy way but return just the most recent therapy
  return {
    batch_id: mostRecentBatch.batch_id,
    therapy: mostRecentTherapy.therapy,
  };
};

// Storage key constant
const ADHERENCE_STORAGE_KEY = "lastFetchedAdherence";
/**
 * Extract date range from adherence data sessions
 * @param sessions - Array of session data
 * @returns Object with startDate and endDate
 */
const extractDateRange = (
  sessions: AdherenceData["sessions"]
): { startDate: string; endDate: string } => {
  if (sessions.length === 0) {
    const today = new Date().toISOString().split("T")[0];
    return { startDate: today, endDate: today };
  }

  const dates = sessions.map((session) => session.date).sort();
  return {
    startDate: dates[0],
    endDate: dates[dates.length - 1],
  };
};

/**
 * Store adherence data with device ID (date range extracted from adherence data)
 * @param storage - Ionic storage instance
 * @param adherenceData - The adherence data to store
 * @param deviceId - The device ID
 */
export const storeAdherenceData = async (
  storage: Storage,
  adherenceData: AdherenceData,
  deviceId: string
): Promise<void> => {
  try {
    const dateRange = extractDateRange(adherenceData.sessions);

    const storedData: StoredAdherenceData = {
      adherenceData,
      deviceId,
      dateRange,
      fetchedAt: new Date().toISOString(),
    };

    await storage.set(ADHERENCE_STORAGE_KEY, storedData);
    console.log("Adherence data stored successfully");
  } catch (error) {
    console.error("Error storing adherence data:", error);
    throw error;
  }
};

/**
 * Retrieve the last fetched adherence data
 * @param storage - Ionic storage instance
 * @returns Promise<StoredAdherenceData | null> - The stored adherence data or null if not found
 */
export const getLastFetchedAdherence = async (
  storage: Storage
): Promise<StoredAdherenceData | null> => {
  try {
    const storedData = await storage.get(ADHERENCE_STORAGE_KEY);
    return storedData || null;
  } catch (error) {
    console.error("Error retrieving adherence data:", error);
    throw error;
  }
};

/**
 * Get adherence data by device ID (checks if the stored data matches the device ID)
 * @param storage - Ionic storage instance
 * @param deviceId - The device ID to check
 * @returns Promise<StoredAdherenceData | null> - The stored adherence data if device ID matches, null otherwise
 */
export const getAdherenceByDeviceId = async (
  storage: Storage,
  deviceId: string
): Promise<StoredAdherenceData | null> => {
  try {
    const storedData = await getLastFetchedAdherence(storage);

    if (storedData && storedData.deviceId === deviceId) {
      return storedData;
    }

    return null;
  } catch (error) {
    console.error("Error retrieving adherence data by device ID:", error);
    throw error;
  }
};

/**
 * Check if stored adherence data exists and is for a specific device
 * @param storage - Ionic storage instance
 * @param deviceId - Optional device ID to check
 * @returns Promise<boolean> - True if data exists (and matches device ID if provided)
 */
export const hasAdherenceData = async (
  storage: Storage,
  deviceId?: string
): Promise<boolean> => {
  try {
    const storedData = await getLastFetchedAdherence(storage);

    if (!storedData) {
      return false;
    }

    if (deviceId) {
      return storedData.deviceId === deviceId;
    }

    return true;
  } catch (error) {
    console.error("Error checking adherence data existence:", error);
    return false;
  }
};

/**
 * Clear stored adherence data
 * @param storage - Ionic storage instance
 * @returns Promise<void>
 */
export const clearAdherenceData = async (storage: Storage): Promise<void> => {
  try {
    await storage.remove(ADHERENCE_STORAGE_KEY);
    console.log("Adherence data cleared successfully");
  } catch (error) {
    console.error("Error clearing adherence data:", error);
    throw error;
  }
};

const TITRATION_STORAGE_KEY = "storedTitrationSessions";

/**
 * Store titration data with device ID and refId
 * @param storage - Ionic storage instance
 * @param titrationData - The titration data to store
 * @param deviceId - The device ID
 * @param refId - The reference ID for the session
 */
export const storeTitrationSession = async (
  storage: Storage,
  titrationSession: TitrationSession,
  titrationData: TitrationData,
  deviceId: string
): Promise<void> => {
  try {
    // Get existing sessions
    const existingSessions = await getAllTitrationSessions(storage);

    // Check if this is a new device (different from any existing sessions)
    const isNewDevice =
      existingSessions.length > 0 &&
      !existingSessions.some((session) => session.deviceId === deviceId);

    let updatedSessions: StoredTitrationSession[];

    if (isNewDevice) {
      // New device: clear all previous data and start fresh
      updatedSessions = [];
    } else {
      // Same device or first time: keep existing sessions for this device
      updatedSessions = existingSessions.filter(
        (session) => session.deviceId === deviceId
      );
    }

    // Check if this refId already exists for this device
    const existingIndex = updatedSessions.findIndex(
      (session) =>
        session.session.refId === titrationSession.refId &&
        session.deviceId === deviceId
    );

    const newSession: StoredTitrationSession = {
      session: titrationSession,
      deviceId,
      titrationData,
      fetchedAt: new Date().toISOString(),
    };

    if (existingIndex >= 0) {
      // Update existing session
      updatedSessions[existingIndex] = newSession;
    } else {
      // Add new session
      updatedSessions.push(newSession);

      // If we exceed 5 sessions, remove the one with smallest refId for this device
      if (updatedSessions.length > 5) {
        const deviceSessions = updatedSessions.filter(
          (s) => s.deviceId === deviceId
        );
        deviceSessions.sort(
          (a, b) => parseInt(a.session.refId) - parseInt(b.session.refId)
        );
        const sessionToRemove = deviceSessions[0];

        updatedSessions = updatedSessions.filter(
          (s) =>
            !(
              s.session.refId === sessionToRemove.session.refId &&
              s.deviceId === sessionToRemove.deviceId
            )
        );
      }
    }

    await storage.set(TITRATION_STORAGE_KEY, updatedSessions);
    console.log(
      `Titration session stored successfully - RefId: ${titrationSession.refId}, DeviceId: ${deviceId}`
    );
  } catch (error) {
    console.error("Error storing titration session:", error);
    throw error;
  }
};

/**
 * Get all stored titration sessions
 * @param storage - Ionic storage instance
 * @returns Promise<StoredTitrationSession[]> - Array of all stored sessions
 */
export const getAllTitrationSessions = async (
  storage: Storage
): Promise<StoredTitrationSession[]> => {
  try {
    const storedSessions = await storage.get(TITRATION_STORAGE_KEY);
    console.log("Retrieved titration sessions:", storedSessions);
    return Array.isArray(storedSessions) ? storedSessions : [];
  } catch (error) {
    console.error("Error retrieving all titration sessions:", error);
    throw error;
  }
};

/**
 * Get titration session by refId
 * @param storage - Ionic storage instance
 * @param refId - The reference ID to search for
 * @returns Promise<StoredTitrationSession | null> - The session if found, null otherwise
 */
export const getTitrationSessionByRefId = async (
  storage: Storage,
  refId: string
): Promise<StoredTitrationSession | null> => {
  try {
    const sessions = await getAllTitrationSessions(storage);
    return sessions.find((session) => session.session.refId === refId) || null;
  } catch (error) {
    console.error("Error retrieving titration session by refId:", error);
    throw error;
  }
};

/**
 * Get titration session by refId and deviceId
 * @param storage - Ionic storage instance
 * @param refId - The reference ID to search for
 * @param deviceId - The device ID to match
 * @returns Promise<StoredTitrationSession | null> - The session if found, null otherwise
 */
export const getTitrationSessionByRefIdAndDevice = async (
  storage: Storage,
  refId: string,
  deviceId: string
): Promise<StoredTitrationSession | null> => {
  try {
    const sessions = await getAllTitrationSessions(storage);
    return (
      sessions.find(
        (session) =>
          session.session.refId === refId && session.deviceId === deviceId
      ) || null
    );
  } catch (error) {
    console.error(
      "Error retrieving titration session by refId and deviceId:",
      error
    );
    throw error;
  }
};

/**
 * Get all titration sessions for a specific device
 * @param storage - Ionic storage instance
 * @param deviceId - The device ID to filter by
 * @returns Promise<StoredTitrationSession[]> - Array of sessions for the device
 */
export const getTitrationSessionsByDeviceId = async (
  storage: Storage,
  deviceId: string
): Promise<StoredTitrationSession[]> => {
  try {
    const sessions = await getAllTitrationSessions(storage);
    return sessions.filter((session) => session.deviceId === deviceId);
  } catch (error) {
    console.error("Error retrieving titration sessions by deviceId:", error);
    throw error;
  }
};

/**
 * Get the most recent titration session (highest refId)
 * @param storage - Ionic storage instance
 * @returns Promise<StoredTitrationSession | null> - The most recent session or null
 */
export const getLatestTitrationSession = async (
  storage: Storage
): Promise<StoredTitrationSession | null> => {
  try {
    const allSessions = await getAllTitrationSessions(storage);

    if (allSessions.length === 0) {
      return null;
    }

    // Sort by refId (highest first) and return the most recent
    allSessions.sort(
      (a, b) => parseInt(b.session.refId) - parseInt(a.session.refId)
    );
    return allSessions[0];
  } catch (error) {
    console.error("Error retrieving latest titration session:", error);
    throw error;
  }
};

/**
 * Check if a titration session exists by refId
 * @param storage - Ionic storage instance
 * @param refId - The reference ID to check
 * @returns Promise<boolean> - True if session exists
 */
export const hasTitrationSessionByRefId = async (
  storage: Storage,
  refId: string
): Promise<boolean> => {
  try {
    const session = await getTitrationSessionByRefId(storage, refId);
    return session !== null;
  } catch (error) {
    console.error("Error checking titration session existence:", error);
    return false;
  }
};

/**
 * Check if a titration session exists by refId and deviceId
 * @param storage - Ionic storage instance
 * @param refId - The reference ID to check
 * @param deviceId - The device ID to check
 * @returns Promise<boolean> - True if session exists for the specific device
 */
export const hasTitrationSessionByRefIdAndDevice = async (
  storage: Storage,
  refId: string,
  deviceId: string
): Promise<boolean> => {
  try {
    const sessions = await getAllTitrationSessions(storage);
    return sessions.some(
      (session) =>
        session.session.refId === refId && session.deviceId === deviceId
    );
  } catch (error) {
    console.error(
      "Error checking titration session existence by refId and deviceId:",
      error
    );
    return false;
  }
};

/**
 * Check if any titration sessions exist for a device
 * @param storage - Ionic storage instance
 * @param deviceId - Optional device ID to check
 * @returns Promise<boolean> - True if sessions exist
 */
export const hasTitrationSessionsForDevice = async (
  storage: Storage,
  deviceId?: string
): Promise<boolean> => {
  try {
    if (deviceId) {
      const deviceSessions = await getTitrationSessionsByDeviceId(
        storage,
        deviceId
      );
      return deviceSessions.length > 0;
    } else {
      const allSessions = await getAllTitrationSessions(storage);
      return allSessions.length > 0;
    }
  } catch (error) {
    console.error("Error checking titration sessions existence:", error);
    return false;
  }
};

/**
 * Remove a specific titration session by refId
 * @param storage - Ionic storage instance
 * @param refId - The reference ID to remove
 * @returns Promise<boolean> - True if session was removed, false if not found
 */
export const removeTitrationSessionByRefId = async (
  storage: Storage,
  refId: string
): Promise<boolean> => {
  try {
    const sessions = await getAllTitrationSessions(storage);
    const filteredSessions = sessions.filter(
      (session) => session.session.refId !== refId
    );

    if (filteredSessions.length === sessions.length) {
      return false; // Session not found
    }

    await storage.set(TITRATION_STORAGE_KEY, filteredSessions);
    console.log(`Titration session removed - RefId: ${refId}`);
    return true;
  } catch (error) {
    console.error("Error removing titration session:", error);
    throw error;
  }
};

/**
 * Clear all stored titration sessions
 * @param storage - Ionic storage instance
 * @returns Promise<void>
 */
export const clearAllTitrationSessions = async (
  storage: Storage
): Promise<void> => {
  try {
    await storage.remove(TITRATION_STORAGE_KEY);
    console.log("All titration sessions cleared successfully");
  } catch (error) {
    console.error("Error clearing titration sessions:", error);
    throw error;
  }
};

/**
 * Get session count for a device
 * @param storage - Ionic storage instance
 * @param deviceId - The device ID
 * @returns Promise<number> - Number of sessions for the device
 */
export const getTitrationSessionCount = async (
  storage: Storage,
  deviceId: string
): Promise<number> => {
  try {
    const deviceSessions = await getTitrationSessionsByDeviceId(
      storage,
      deviceId
    );
    return deviceSessions.length;
  } catch (error) {
    console.error("Error getting session count:", error);
    return 0;
  }
};

const CURVE_ANALYSIS_STORAGE_KEY = "curveAnalysisSessions";

/**
 * Store curve analysis session data with device management
 * - If new device: clear all existing sessions and store new one
 * - If same device and < 3 sessions: add new session
 * - If same device and 3 sessions: remove session with lowest refId, add new one
 * @param storage - Ionic storage instance
 * @param therapySession - The therapy session data
 * @param curveAnalysisData - The curve analysis data
 * @param deviceId - The device ID
 */
export const storeCurveAnalysisSession = async (
  storage: Storage,
  therapySession: TherapySession,
  curveAnalysisData: CurveAnalysisData,
  deviceId: string
): Promise<void> => {
  try {
    // Get existing data
    const existingData = (await storage.get(
      CURVE_ANALYSIS_STORAGE_KEY
    )) as StoredCurveAnalysisData | null;

    const newSession: StoredCurveSession = {
      therapySession: {
        ...therapySession,
        locallyAvailable: true,
      },
      curveAnalysisData,
      deviceId,
      fetchedAt: new Date().toISOString(),
    };

    let updatedData: StoredCurveAnalysisData;

    // If no existing data or different device, start fresh
    if (!existingData || existingData.currentDeviceId !== deviceId) {
      updatedData = {
        sessions: [newSession],
        currentDeviceId: deviceId,
      };
    } else {
      // Same device - manage sessions (max 3)
      let sessions = [...existingData.sessions];

      if (sessions.length < 5) {
        // Less than 5 sessions, just add new one
        sessions.push(newSession);
      } else {
        // Already 5 sessions, remove one with lowest refId
        const sessionWithLowestRefId = sessions.reduce((lowest, current) =>
          Number(current.therapySession.refId) <
          Number(lowest.therapySession.refId)
            ? current
            : lowest
        );

        // Remove session with lowest refId and add new session
        sessions = sessions.filter(
          (session) =>
            session.therapySession.refId !==
            sessionWithLowestRefId.therapySession.refId
        );
        sessions.push(newSession);
      }

      updatedData = {
        sessions,
        currentDeviceId: deviceId,
      };
    }

    await storage.set(CURVE_ANALYSIS_STORAGE_KEY, updatedData);
    console.log("Curve analysis session stored successfully");
  } catch (error) {
    console.error("Error storing curve analysis session:", error);
    throw error;
  }
};

/**
 * Get all curve analysis sessions for the current device
 * @param storage - Ionic storage instance
 * @param deviceId - Optional device ID to filter by (if not provided, returns sessions for current device)
 * @returns Promise<StoredCurveSession[]> - Array of stored sessions for the device
 */
export const getAllCurveAnalysisSessions = async (
  storage: Storage,
  deviceId?: string
): Promise<StoredCurveSession[]> => {
  try {
    const storedData = (await storage.get(
      CURVE_ANALYSIS_STORAGE_KEY
    )) as StoredCurveAnalysisData | null;

    if (!storedData) {
      return [];
    }

    // Filter by deviceId if provided, otherwise return sessions for current device
    const targetDeviceId = deviceId || storedData.currentDeviceId;
    return storedData.sessions.filter(
      (session) => session.deviceId === targetDeviceId
    );
  } catch (error) {
    console.error("Error retrieving all curve analysis sessions:", error);
    throw error;
  }
};

/**
 * Get curve analysis session by session ID (only from current device)
 * @param storage - Ionic storage instance
 * @param sessionId - The therapy session ID
 * @returns Promise<StoredCurveSession | null> - The session if found and from current device, null otherwise
 */
export const getCurveAnalysisSessionById = async (
  storage: Storage,
  sessionId: string
): Promise<StoredCurveSession | null> => {
  try {
    const storedData = (await storage.get(
      CURVE_ANALYSIS_STORAGE_KEY
    )) as StoredCurveAnalysisData | null;

    if (!storedData) {
      return null;
    }

    // Only return session if it's from the current device
    const session = storedData.sessions.find(
      (s) =>
        s.therapySession.refId === sessionId &&
        s.deviceId === storedData.currentDeviceId
    );

    return session || null;
  } catch (error) {
    console.error("Error retrieving curve analysis session by ID:", error);
    throw error;
  }
};

/**
 * Get curve analysis sessions by device ID
 * @param storage - Ionic storage instance
 * @param deviceId - The device ID
 * @returns Promise<StoredCurveSession[]> - Array of sessions for the specified device
 */
export const getCurveAnalysisSessionsByDevice = async (
  storage: Storage,
  deviceId: string
): Promise<StoredCurveSession[]> => {
  try {
    const storedData = (await storage.get(
      CURVE_ANALYSIS_STORAGE_KEY
    )) as StoredCurveAnalysisData | null;

    if (!storedData) {
      return [];
    }

    return storedData.sessions.filter(
      (session) => session.deviceId === deviceId
    );
  } catch (error) {
    console.error("Error retrieving curve analysis sessions by device:", error);
    throw error;
  }
};

/**
 * Check if curve analysis data exists for current device
 * @param storage - Ionic storage instance
 * @param deviceId - Optional device ID to check
 * @returns Promise<boolean> - True if sessions exist for the device
 */
export const hasCurveAnalysisData = async (
  storage: Storage,
  deviceId?: string
): Promise<boolean> => {
  try {
    const sessions = await getAllCurveAnalysisSessions(storage, deviceId);
    return sessions.length > 0;
  } catch (error) {
    console.error("Error checking curve analysis data existence:", error);
    return false;
  }
};

/**
 * Clear all stored curve analysis data
 * @param storage - Ionic storage instance
 * @returns Promise<void>
 */
export const clearCurveAnalysisData = async (
  storage: Storage
): Promise<void> => {
  try {
    await storage.remove(CURVE_ANALYSIS_STORAGE_KEY);
    console.log("Curve analysis data cleared successfully");
  } catch (error) {
    console.error("Error clearing curve analysis data:", error);
    throw error;
  }
};
// Fixed storage functions

/**
 * Home Page - Get all connected devices
 * Daily Status - Save and retrieve daily status data
 **/

// Generate a unique session ID for tab isolation
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session ID (stored in sessionStorage for tab isolation)
const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem("SESSION_ID");

  if (!sessionId) {
    sessionId = crypto.randomUUID(); // Creates a unique ID
    sessionStorage.setItem("SESSION_ID", sessionId);
  }

  return sessionId;
};

// Create isolated storage key (tab-specific only)
const createIsolatedKey = (baseKey: string): string => {
  const sessionId = getSessionId();
  return `${baseKey}_${sessionId}`;
};

// Generic storage setter with tab isolation only
const setIsolatedStorage = async (
  storage: Storage,
  baseKey: string,
  value: any
): Promise<void> => {
  try {
    const isolatedKey = baseKey;
    const stringValue =
      typeof value === "string" ? value : JSON.stringify(value);

    storage && (await storage.set(isolatedKey, stringValue));
    // console.log(`Successfully saved data for isolated key: ${isolatedKey}`);
  } catch (error) {
    console.error(`Error setting data for key ${baseKey}:`, error);
    throw error;
  }
};

// Generic storage getter with tab isolation only
const getIsolatedStorage = async (
  storage: Storage,
  baseKey: string
): Promise<any> => {
  try {
    const isolatedKey = baseKey;
    const result = await storage.get(isolatedKey);

    if (result) {
      try {
        return JSON.parse(result);
      } catch (parseError) {
        console.log(
          `Value for key ${isolatedKey} is not JSON, returning raw value`
        );
        return result;
      }
    }
    return null;
  } catch (error) {
    console.error(`Error getting data for key ${baseKey}:`, error);
    return null;
  }
};

// Generic storage remover with tab isolation only
const removeIsolatedStorage = async (
  storage: Storage,
  baseKey: string
): Promise<void> => {
  try {
    const isolatedKey = createIsolatedKey(baseKey);
    await storage.remove(isolatedKey);
    console.log(`Successfully removed data for isolated key: ${isolatedKey}`);
  } catch (error) {
    console.error(`Error removing data for key ${baseKey}:`, error);
    throw error;
  }
};

export const ionicStorage = async (
  storage: any,
  key: string,
  value: any
): Promise<void> => {
  return setIsolatedStorage(storage, key, value);
};

export const getIonicStorage = async (
  storage: any,
  key: string
): Promise<any> => {
  return getIsolatedStorage(storage, key);
};

export const removeIonicStorage = async (
  storage: any,
  key: string
): Promise<void> => {
  return storage.remove(key);
};

// CLEANUP FUNCTIONS

// Clear all data for current session/tab (call this on logout)
export const clearCurrentTabData = async (storage: Storage): Promise<void> => {
  try {
    const sessionId = getSessionId();
    const allKeys = await storage.keys();

    // Remove all keys that contain the current session ID
    const sessionKeys = allKeys.filter((key) => key.includes(sessionId));

    for (const key of sessionKeys) {
      await storage.remove(key);
    }

    // Clear session storage to remove session ID
    sessionStorage.removeItem("app_session_id");

    console.log(
      `Cleared ${sessionKeys.length} items for current tab session ${sessionId}`
    );
  } catch (error) {
    console.error("Error clearing current tab data:", error);
  }
};

// Clear all storage data (complete reset - use carefully)
export const clearAllStorageData = async (storage: Storage): Promise<void> => {
  try {
    await storage.clear();
    sessionStorage.removeItem("app_session_id");
    console.log("Cleared all storage data");
  } catch (error) {
    console.error("Error clearing all storage data:", error);
  }
};

// Cleanup old sessions (call periodically to prevent storage bloat)
export const cleanupOldSessions = async (
  storage: Storage,
  daysOld = 7
): Promise<void> => {
  try {
    const allKeys = await storage.keys();
    const cutoffTime = Date.now() - daysOld * 24 * 60 * 60 * 1000;

    const oldSessionKeys = allKeys.filter((key) => {
      const sessionMatch = key.match(/session_(\d+)_/);
      if (sessionMatch) {
        const sessionTime = parseInt(sessionMatch[1]);
        return sessionTime < cutoffTime;
      }
      return false;
    });

    for (const key of oldSessionKeys) {
      await storage.remove(key);
    }

    console.log(`Cleaned up ${oldSessionKeys.length} old session items`);
  } catch (error) {
    console.error("Error cleaning up old sessions:", error);
  }
};

// Get current session info (for debugging purposes)
export const getCurrentSessionInfo = (): {
  sessionId: string;
  timestamp: number;
} => {
  const sessionId = getSessionId();
  const sessionMatch = sessionId.match(/session_(\d+)_/);
  const timestamp = sessionMatch ? parseInt(sessionMatch[1]) : Date.now();

  return {
    sessionId,
    timestamp,
  };
};

// Check if storage has data for current session
export const hasCurrentSessionData = async (
  storage: Storage
): Promise<boolean> => {
  try {
    const sessionId = getSessionId();
    const allKeys = await storage.keys();
    return allKeys.some((key) => key.includes(sessionId));
  } catch (error) {
    console.error("Error checking session data:", error);
    return false;
  }
};

// UTILITY: Force new session (useful for testing or manual session reset)
export const forceNewSession = (): string => {
  sessionStorage.removeItem("app_session_id");
  return getSessionId(); // This will generate a new session ID
};

// Storage key for titration session list
const TITRATION_SESSION_LIST_STORAGE_KEY = "titration_session_list";

/**
 * Store titration session list - clears previous data if from different device
 * @param storage - Ionic storage instance
 * @param sessionList - Array of titration sessions to store
 * @param deviceId - The device ID
 */
export const storeTitrationSessionList = async (
  storage: Storage,
  sessionList: StoredTitrationSessionsList[],
  deviceId: string
): Promise<void> => {
  try {
    // Check if previous data exists and is from a different device
    const existingData: StoredTitrationSessionsList[] =
      (await storage.get(TITRATION_SESSION_LIST_STORAGE_KEY)) || [];

    if (existingData.length > 0 && existingData[0].deviceId !== deviceId) {
      console.log(
        `New device detected (${deviceId}), clearing previous session list from device: ${existingData[0].deviceId}`
      );
    }

    // Ensure all sessions have the correct deviceId
    const sessionsWithDeviceId = sessionList.map((session) => ({
      ...session,
      deviceId: deviceId,
    }));

    await storage.set(TITRATION_SESSION_LIST_STORAGE_KEY, sessionsWithDeviceId);
    console.log(
      `Titration session list stored successfully for device: ${deviceId}, sessions: ${sessionList.length}`
    );
  } catch (error) {
    console.error("Error storing titration session list:", error);
    throw error;
  }
};

/**
 * Get stored titration session list
 * @param storage - Ionic storage instance
 * @returns Promise<StoredTitrationSessionsList[]> - Array of stored titration sessions, empty array if none found
 */
export const getTitrationSessionList = async (
  storage: Storage
): Promise<StoredTitrationSessionsList[]> => {
  try {
    const storedData: StoredTitrationSessionsList[] = await storage.get(
      TITRATION_SESSION_LIST_STORAGE_KEY
    );
    return storedData || [];
  } catch (error) {
    console.error("Error retrieving titration session list:", error);
    return [];
  }
};
